import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CoverSlide } from '../../pages/deck/CoverSlide';
import { ProblemSlide } from '../../pages/deck/ProblemSlide';
import { SolutionSlide } from '../../pages/deck/SolutionSlide';
import { ProductExperienceSlide } from '../../pages/deck/ProductExperienceSlide';
import { CallerExperienceSlide } from '../../pages/deck/CallerExperienceSlide';
import { EmergencySlide } from '../../pages/deck/EmergencySlide';
import { AlwaysAvailableSlide } from '../../pages/deck/AlwaysAvailableSlide';
import { PlatformSlide } from '../../pages/deck/PlatformSlide';
import { CompetitiveSlide } from '../../pages/deck/CompetitiveSlide';
import { MarketSlide } from '../../pages/deck/MarketSlide';
import { GoToMarketSlide } from '../../pages/deck/GoToMarketSlide';
import { BusinessModelSlide } from '../../pages/deck/BusinessModelSlide';
import { FinancialOutlookSlide } from '../../pages/deck/FinancialOutlookSlide';
import { PlatformExpansionSlide } from '../../pages/deck/PlatformExpansionSlide';
import { TelecomDistributionSlide } from '../../pages/deck/TelecomDistributionSlide';
import { TeamSlide } from '../../pages/deck/TeamSlide';
import { RaiseSlide } from '../../pages/deck/RaiseSlide';
import { InvestmentThesisSlide } from '../../pages/deck/InvestmentThesisSlide';
import { ThankYouSlide } from '../../pages/deck/ThankYouSlide';
interface DeckSlide {
  id: string;
  label: string;
  path: string;
  Component: () => JSX.Element;
}
export const DECK_SLIDES: DeckSlide[] = [{
  id: '01',
  label: 'Cover',
  path: '/deck/1',
  Component: CoverSlide
}, {
  id: '02',
  label: 'The Problem',
  path: '/deck/2',
  Component: ProblemSlide
}, {
  id: '03',
  label: 'The Solution',
  path: '/deck/3',
  Component: SolutionSlide
}, {
  id: '04',
  label: 'Product Experience',
  path: '/deck/4',
  Component: ProductExperienceSlide
}, {
  id: '05',
  label: 'Caller Experience',
  path: '/deck/5',
  Component: CallerExperienceSlide
}, {
  id: '06',
  label: 'Emergency Detection',
  path: '/deck/6',
  Component: EmergencySlide
}, {
  id: '07',
  label: 'Always Available',
  path: '/deck/7',
  Component: AlwaysAvailableSlide
}, {
  id: '08',
  label: 'VEXU Business',
  path: '/deck/8',
  Component: PlatformSlide
}, {
  id: '09',
  label: 'Competitive Positioning',
  path: '/deck/9',
  Component: CompetitiveSlide
}, {
  id: '10',
  label: 'Market Opportunity',
  path: '/deck/10',
  Component: MarketSlide
}, {
  id: '11',
  label: 'Go-to-Market',
  path: '/deck/11',
  Component: GoToMarketSlide
}, {
  id: '12',
  label: 'Business Model',
  path: '/deck/12',
  Component: BusinessModelSlide
}, {
  id: '13',
  label: 'Financial Outlook',
  path: '/deck/13',
  Component: FinancialOutlookSlide
}, {
  id: '14',
  label: 'Platform Expansion',
  path: '/deck/14',
  Component: PlatformExpansionSlide
}, {
  id: '15',
  label: 'Telecom Distribution',
  path: '/deck/15',
  Component: TelecomDistributionSlide
}, {
  id: '16',
  label: 'Team',
  path: '/deck/16',
  Component: TeamSlide
}, {
  id: '17',
  label: 'The Raise',
  path: '/deck/17',
  Component: RaiseSlide
}, {
  id: '18',
  label: 'Investment Thesis',
  path: '/deck/18',
  Component: InvestmentThesisSlide
}, {
  id: '19',
  label: 'Thank You',
  path: '/deck/19',
  Component: ThankYouSlide
}];
interface DeckShellProps {
  initialIndex?: number;
}

/** Presentation runner: arrow keys, click, or the corner controls move slides. */
export function DeckShell({
  initialIndex = 0
}: DeckShellProps) {
  const [index, setIndex] = useState(initialIndex);
  const go = useCallback((next: number) => {
    setIndex(prev => {
      const clamped = Math.min(Math.max(next, 0), DECK_SLIDES.length - 1);
      if (clamped !== prev && typeof window !== 'undefined') {
        window.history.replaceState(null, '', DECK_SLIDES[clamped].path);
      }
      return clamped;
    });
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) {
        e.preventDefault();
        setIndex(prev => {
          const next = Math.min(prev + 1, DECK_SLIDES.length - 1);
          window.history.replaceState(null, '', DECK_SLIDES[next].path);
          return next;
        });
      }
      if (['ArrowLeft', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        setIndex(prev => {
          const next = Math.max(prev - 1, 0);
          window.history.replaceState(null, '', DECK_SLIDES[next].path);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A simulated tap inside an embedded app screen can nudge the document's
  // scroll position; reset it whenever the slide changes so the scaled canvas
  // is never left offset.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [index]);
  const {
    Component
  } = DECK_SLIDES[index];
  return <div className="relative w-full h-full bg-[#EEF2ED]" data-id="element-200" data-mp="1czdvnb4snnis">
      {/* Each slide mounts fresh and unmounts immediately on change — an exit
          animation here can strand a slide that owns running timers. */}
      <motion.div key={index} className="absolute inset-0" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4,
      ease: [0.22, 0.61, 0.36, 1]
    }} data-id="element-201" data-mp="1my3dj3wox7z1">
        <Component data-id="element-202" data-mp="1woegan4n1b4c" />
      </motion.div>

      {/* Slide controls */}
      <nav aria-label="Slide navigation" className="deck-type absolute bottom-6 right-6 z-50 flex items-center gap-3 rounded-full bg-white/70 px-3 py-2 backdrop-blur-xl ring-1 ring-[#0B1712]/[0.07] shadow-[0_8px_24px_-12px_rgba(11,23,18,0.25)]" data-id="element-203" data-mp="2amct3o3b8zhq">
        <button type="button" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous slide" className="grid h-7 w-7 place-items-center rounded-full text-[#33463B] transition-colors hover:bg-[#0B1712]/[0.06] disabled:opacity-25 disabled:hover:bg-transparent" data-id="element-204" data-mp="138k63c6ad743">
          <ChevronLeft className="h-4 w-4" data-id="element-205" data-mp="1m1o397ipq23g" />
        </button>
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5A6B60] tabular-nums" data-id="element-206" data-mp="1m37jiy4dcd58">
          {DECK_SLIDES[index].id} / {String(DECK_SLIDES.length).padStart(2, '0')}
        </span>
        <button type="button" onClick={() => go(index + 1)} disabled={index === DECK_SLIDES.length - 1} aria-label="Next slide" className="grid h-7 w-7 place-items-center rounded-full text-[#33463B] transition-colors hover:bg-[#0B1712]/[0.06] disabled:opacity-25 disabled:hover:bg-transparent" data-id="element-207" data-mp="200nsaga50j05">
          <ChevronRight className="h-4 w-4" data-id="element-208" data-mp="3u99qaqguu2ni" />
        </button>
      </nav>
    </div>;
}